import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Card,
  CardContent,
  TableSortLabel,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MobileTableSort from './MobileTableSort';

const stickyActionsCellSx = (theme) => ({
  position: 'sticky',
  right: 0,
  backgroundColor: theme.palette.background.paper,
  zIndex: 1,
  boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
  whiteSpace: 'nowrap',
});

const stickyActionsHeaderSx = (theme) => ({
  ...stickyActionsCellSx(theme),
  zIndex: 3,
});

/** Scroll area ≈ full window minus app chrome, filters, tabs, pagination (Inventory). */
export const VIEWPORT_TABLE_MAX_HEIGHT = 'max(200px, calc(100dvh - 280px))';

const DEFAULT_TABLE_MAX_HEIGHT = VIEWPORT_TABLE_MAX_HEIGHT;

const ResponsiveTable = memo(({
  columns,
  data,
  renderMobileCard,
  getStatusColor,
  sortBy,
  sortOrder,
  onSort,
  defaultSortBy,
  defaultSortOrder,
  onResetSort,
  sortColumnFilter,
  tableMaxHeight = DEFAULT_TABLE_MAX_HEIGHT,
  onRowClick,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lastColumnIndex = columns.length - 1;

  const mobileScrollCap =
    tableMaxHeight != null && tableMaxHeight !== false && tableMaxHeight !== 'none' && tableMaxHeight !== '';

  if (isMobile && renderMobileCard) {
    const sortableForMobile = sortColumnFilter
      ? columns.filter((column) => sortColumnFilter(column.id))
      : columns;
    const showMobileSort = sortableForMobile.some((column) => column.sortable) && onSort;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pr: 0.5,
          ...(mobileScrollCap ? { maxHeight: tableMaxHeight, overflowY: 'auto' } : {}),
        }}
      >
        {showMobileSort && (
          <MobileTableSort
            columns={sortableForMobile}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
            defaultSortBy={defaultSortBy}
            defaultSortOrder={defaultSortOrder}
            onResetSort={onResetSort}
          />
        )}
        {data.map((row, index) => (
          <Card
            key={row._id || row.id || index}
            elevation={2}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            sx={{
              flexShrink: 0,
              ...(onRowClick ? { cursor: 'pointer' } : {}),
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              {renderMobileCard(row, getStatusColor)}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  const tableScrollCap =
    tableMaxHeight != null && tableMaxHeight !== false && tableMaxHeight !== 'none' && tableMaxHeight !== '';

  return (
    <TableContainer
      component={Paper}
      sx={{
        ...(tableScrollCap ? { maxHeight: tableMaxHeight, overflow: 'auto' } : { overflow: 'auto' }),
      }}
    >
      <Table stickyHeader sx={{ minWidth: 650 }} size="small">
        <TableHead>
          <TableRow>
            {columns.map((column, idx) => (
              <TableCell
                key={column.id}
                align={column.align || 'left'}
                sortDirection={column.sortable && sortBy === column.id ? sortOrder : false}
                sx={[
                  {
                    fontWeight: 'bold',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    whiteSpace: 'nowrap',
                    backgroundColor: 'background.paper',
                  },
                  idx === lastColumnIndex ? stickyActionsHeaderSx(theme) : {},
                ]}
              >
                {column.sortable && onSort ? (
                  <TableSortLabel
                    active={sortBy === column.id}
                    direction={sortBy === column.id ? sortOrder : 'asc'}
                    onClick={() => onSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              key={index}
              hover
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              sx={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((column, idx) => (
                <TableCell
                  key={column.id}
                  align={column.align || 'left'}
                  sx={[
                    {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      py: { xs: 1, sm: 1.5 },
                    },
                    idx === lastColumnIndex ? stickyActionsCellSx(theme) : {},
                  ]}
                >
                  {column.render ? column.render(row) : row[column.id]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

ResponsiveTable.displayName = 'ResponsiveTable';

export default ResponsiveTable;
